import { AlbumGrid } from "../../components/game/AlbumGrid";
import { PageSkeleton } from "../../components/brand/Experience";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, LineChart } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDateTime, LIMB_FR, MODE_FR, SIDE_FR, STATUS_FR } from "../../lib/api";
import { BALANCE, DIAG, HAND, WALK } from "../../lib/labels";
import { usePatient, useSessions } from "../../hooks/useData";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader, Panel, SimBadge } from "./CareLayout";
import { PatientForm } from "./PatientForm";
import { SavannaCard } from "./SavannaCard";

const Row = ({ k, v }) => <div className="flex justify-between border-b border-border/60 py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-semibold text-right">{v}</span></div>;

export default function PatientDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: p } = usePatient(pid);
  const { data: sessions } = useSessions(pid);
  if (!p) return <PageSkeleton />;
  const cp = p.clinical_profile, rs = p.restrictions;
  const save = async (f) => {
    await api.put(`/patients/${pid}`, { name: f.name, age: f.age, clinical_profile: f.clinical_profile, restrictions: f.restrictions, care_plan: f.care_plan });
    qc.invalidateQueries({ queryKey: ["patient", pid] });
    qc.invalidateQueries({ queryKey: ["patients"] });
    toast.success("Profil clinique mis à jour");
  };
  return (
    <div data-testid="patient-detail">
      <PageHeader title={p.name} sub={`${p.age} ans · ${cp.diagnosis_label} · Côté atteint : ${SIDE_FR[cp.affected_side]}`}>
        <button data-testid="view-progress-button" onClick={() => nav(`/care/progress?patient=${pid}`)} className="flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-bold"><LineChart className="h-4 w-4" />Progrès</button>
        <button data-testid="configure-session-button" onClick={() => nav(`/care/patients/${pid}/session/new`)} className="flex h-11 items-center gap-2 rounded-xl bg-brand px-4 text-sm font-bold text-white"><CalendarPlus className="h-4 w-4" />Configurer une séance</button>
      </PageHeader>
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 h-auto min-h-11 max-w-full flex-wrap justify-start bg-card [&>button]:min-h-10">
          <TabsTrigger value="profile" data-testid="tab-profile">Profil clinique</TabsTrigger>
          <TabsTrigger value="edit" data-testid="tab-edit">Modifier</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Séances</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <div className="grid gap-6 lg:grid-cols-3">
            <Panel title="Profil clinique" testId="clinical-profile-panel">
              <Row k="Diagnostic" v={DIAG[cp.diagnosis] || cp.diagnosis_label} />
              <Row k="Côté hémiparétique" v={SIDE_FR[cp.affected_side]} />
              <Row k="Membres atteints" v={cp.affected_limbs.map((l) => LIMB_FR[l]).join(", ")} />
              <Row k="Flexion d'épaule max" v={`${cp.shoulder_flexion_max_deg}°`} />
              <Row k="Abduction d'épaule max" v={`${cp.shoulder_abduction_max_deg}°`} />
              <Row k="Amplitude du coude" v={`${cp.elbow_rom_deg}°`} />
              <Row k="Atteinte fonctionnelle" v={`${cp.functional_reach_cm} cm`} />
              <Row k="Préhension" v={HAND[cp.hand_function]} />
              <Row k="Équilibre" v={BALANCE[cp.balance_level]} />
              <Row k="Marche" v={WALK[cp.walking_ability]} />
              <Row k="Fatigue / Douleur" v={`${cp.fatigue_level}/10 · ${cp.pain_level}/10`} />
              <p className="mt-3 rounded-xl bg-primary/10 p-3 text-sm"><b>Objectif :</b> {cp.session_objective}</p>
            </Panel>
            <Panel title="Restrictions thérapeutiques" testId="restrictions-panel">
              <Row k="Amplitude max autorisée" v={`${rs.max_amplitude_pct}%`} />
              <Row k="Durée max de séance" v={`${rs.max_session_duration_min} min`} />
              <Row k="Repos entre missions" v={`${rs.rest_interval_s} s`} />
              <Row k="Mouvements interdits" v={rs.forbidden_movements.length ? rs.forbidden_movements.join(", ") : "Aucun"} />
              <p className="mt-3 text-xs text-muted-foreground">Les restrictions ne sont jamais modifiées automatiquement par l'application.</p>
            </Panel>
            <Panel title="Limites de sécurité appliquées" testId="safe-limits-panel" right={<SimBadge />}>
              <p className="mb-3 text-xs text-muted-foreground">min(paramètre clinique, calibration, restriction). Aucune cible thérapeutique n'est placée au-delà.</p>
              {["right", "left"].map((s) => (
                <div key={s} className="mb-3 rounded-xl bg-muted p-3">
                  <p className="text-sm font-bold">{SIDE_FR[s]} {p.safe_limits[s].is_affected && <span className="ml-1 rounded bg-brand px-1.5 py-0.5 text-[10px] text-white">ATTEINT</span>}</p>
                  <p className="mt-1 font-mono text-xs">Flexion {p.safe_limits[s].shoulder_flexion}° · Abduction {p.safe_limits[s].shoulder_abduction}° · Atteinte {p.safe_limits[s].reach_cm} cm</p>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">Dernière calibration : {fmtDateTime(p.latest_calibration?.created_at)}</p>
            </Panel>
          </div>
          <div className="mt-6 space-y-6"><SavannaCard pid={pid} /><SavannaCard pid={pid} level="river" /><SavannaCard pid={pid} level="forest" /><SavannaCard pid={pid} level="bridge" /><SavannaCard pid={pid} level="village" />
            <Panel title="Album de séances (photos patient)" testId="care-album"><AlbumGrid pid={pid} cols="grid-cols-2 sm:grid-cols-4 lg:grid-cols-6" empty="Aucune photo de fin de séance pour le moment." /></Panel></div>
        </TabsContent>
        <TabsContent value="edit"><PatientForm initial={p} onSubmit={save} /></TabsContent>
        <TabsContent value="sessions">
          <Panel testId="patient-sessions-list">
            {sessions?.map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b border-border/60 py-3 text-sm">
                <span>{fmtDateTime(s.started_at || s.created_at)} · {MODE_FR[s.config.mode]} · {STATUS_FR[s.status]}</span>
                {["completed", "stopped"].includes(s.status) && <Link className="font-semibold text-primary-strong" to={`/care/sessions/${s.id}/report`}>Rapport</Link>}
              </div>
            ))}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
